<template>
    <div class="w-full space-y-4">
        <h2 class="font-semibold text-xl text-center">{{ $t('views.farmToFarm.selectPeriods.title') }}</h2>

        <StepQueryState :is-loading="isLoading" :is-error="isError" @retry="refetch">
            <div class="period-type-choice flex justify-center overflow-x-auto mb-4" role="radiogroup" :aria-label="$t('views.farmToFarm.selectPeriods.title')">
                <button
                    v-for="option of periodTypeOptions"
                    :key="option.value"
                    type="button"
                    role="radio"
                    class="mx-2"
                    :class="{ '--active': option.value === computedState.periodType }"
                    :aria-checked="option.value === computedState.periodType"
                    @click="applyPeriodType(option.value)"
                >
                    {{ option.label }}
                </button>
            </div>

            <div class="flex justify-center h-full">
                <div class="max-w-[80%] w-full h-full">
                    <h3 class="text-center pb-2 font-semibold">{{ periodTypeDescription }}</h3>

                    <CheckboxTree
                        :nodes="nodes"
                        :selected-keys="computedState.selectedPeriods"
                        :expanded-keys="expandedYears"
                        @update:selected-keys="applySelection"
                        @update:expanded-keys="expandedYears = $event"
                    />

                    <p class="pt-2 text-center text-sm text-dark-gray-600">
                        {{ $t('views.farmToFarm.selectPeriods.selectedCount', { count: computedState.selectedPeriods.length }) }}
                    </p>
                </div>
            </div>
        </StepQueryState>

        <div class="flex justify-center space-x-4">
            <CustomButton class="max-w-32" color-preset="secondary" @click="$emit('back')">
                {{ $t('views.reports.resetButtonLabel') }}
            </CustomButton>
<CustomButton :disabled="isLoading || isError || !computedState.selectedPeriods.length" class="max-w-32" @click="$emit('next')">
                {{ $t('views.reports.nextButtonLabel') }}
            </CustomButton>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRef } from 'vue';
import CheckboxTree from '@/components/common/CheckboxTree.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import { useFarmToFarmPeriods } from '@/plugins/store/actions/queries/farm-to-farm';
import { CheckboxTreeNode, FARM_TO_FARM_PERIOD_TYPES, FarmToFarmPeriodOption, FarmToFarmPeriods, FarmToFarmPeriodType, FarmToFarmViewState } from '@/types';
import StepQueryState from '../StepQueryState.vue';

/** Wire name to the list it selects in the API's response. */
const LIST_BY_PERIOD_TYPE: Record<FarmToFarmPeriodType, keyof FarmToFarmPeriods> = {
    Month: 'month',
    Quarter: 'quarter',
    HalfYear: 'half_year',
    Year: 'year',
};

/** Wire name to its own translation suffix, so the locale files read in English rather than in wire spellings. */
const TRANSLATION_KEY_BY_PERIOD_TYPE: Record<FarmToFarmPeriodType, string> = {
    Month: 'month',
    Quarter: 'quarter',
    HalfYear: 'halfYear',
    Year: 'year',
};

/**
 * Step two: choose a granularity, then pick periods from a year tree.
 *
 * Built like the location tree on /reports - expandable parent rows with a tri-state checkbox
 * over child rows - with years as the roots. Ticking a year ticks every period under it, and a
 * partial selection shows as indeterminate rather than as unticked.
 *
 * The API only ever offers fully ended periods, and only those after the selected farms' first
 * registration, so there is nothing to grey out. Whether a farm actually has a registered
 * period-end status is settled downstream in the analytics engine, which estimates one where
 * none exists - the frontend never learns that FctStatus exists.
 *
 * All four granularities arrive in one response, so switching the granularity is instant and
 * costs no request.
 *
 * The granularity control is the same pill group the farm step uses for weaners/slaughter pigs,
 * rather than a Radix RadioGroup: the two steps sit back to back in one wizard, and a pill row
 * followed by a dot-and-label row reads as two unrelated controls.
 */
export default defineComponent({
    components: { CheckboxTree, CustomButton, StepQueryState },
    props: {
        state: {
            type: Object as PropType<FarmToFarmViewState>,
            required: true,
        },
    },
    emits: ['next', 'back', 'update:state'],
    setup(props) {
        const animalGroupId = computed(() => props.state.animalGroupId);
        const farmIds = toRef(props.state, 'selectedFarmIds');

        // Not destructured as `data`: that name collides with the Options API data() option.
        const { data: periods, isLoading, isError, refetch } = useFarmToFarmPeriods(animalGroupId, farmIds);
        return { periods, isLoading, isError, refetch };
    },
    data() {
        return {
            expandedYears: [] as string[],
        };
    },
    computed: {
        /**
         * The shared wizard state, written through rather than assigned to directly. The report
         * steps on /reports use the same indirection, and it keeps every mutation in one shape.
         */
        computedState: {
            get(): FarmToFarmViewState {
                return this.$props.state;
            },
            set(newValue: FarmToFarmViewState) {
                this.$emit('update:state', newValue);
            },
        },
        periodTypeOptions(): { value: FarmToFarmPeriodType; label: string }[] {
            return FARM_TO_FARM_PERIOD_TYPES.map((value) => ({
                value,
                label: this.$t(`views.farmToFarm.periodType.${TRANSLATION_KEY_BY_PERIOD_TYPE[value]}`),
            }));
        },
        /**
         * One complete sentence per granularity rather than a noun interpolated into a shared
         * one. Danish inflects the plural differently for each ("måneder", "kvartaler", "halvår"),
         * and a translator handed a bare noun cannot see the sentence it lands in.
         */
        periodTypeDescription(): string {
            return this.$t(`views.farmToFarm.selectPeriods.description.${TRANSLATION_KEY_BY_PERIOD_TYPE[this.computedState.periodType]}`);
        },
        options(): FarmToFarmPeriodOption[] {
            return this.periods?.[LIST_BY_PERIOD_TYPE[this.computedState.periodType]] ?? [];
        },
        /**
         * Years as parent rows, newest first, each holding its periods in reading order.
         *
         * A year key is the first four characters of every period key in every granularity, so
         * one grouping rule covers all four. For Year the parent and its single child carry the
         * same key, so the tree is flattened instead.
         */
        nodes(): CheckboxTreeNode[] {
            if (this.computedState.periodType === 'Year') {
                return [...this.options].sort((a, b) => b.key.localeCompare(a.key)).map((option) => ({ key: option.key, label: option.label }));
            }

            const byYear = new Map<string, CheckboxTreeNode>();

            this.options.forEach((option) => {
                const year = option.key.slice(0, 4);
                if (!byYear.has(year)) {
                    byYear.set(year, { key: year, label: year, children: [] });
                }
                byYear.get(year)!.children!.push({ key: option.key, label: option.label });
            });

            return [...byYear.values()]
                .sort((a, b) => Number(b.key) - Number(a.key))
                .map((year) => ({
                    ...year,
                    // Newest year first, but periods read naturally within a year.
                    children: [...year.children!].sort((a, b) => a.key.localeCompare(b.key)),
                }));
        },
    },
    watch: {
        /**
         * Drops any selected period the current list no longer offers.
         *
         * Reachable without changing the radio: Back to the farm picker, a different farm set, then
         * Next. The list starts at the earliest registration across the selected farms, so removing
         * the oldest farm can retire periods that are still ticked - and the API would reject them
         * at the very end of the wizard rather than here.
         */
        options: {
            immediate: true,
            handler(options: FarmToFarmPeriodOption[]) {
                // No response yet must preserve the draft; a loaded empty list must clear it.
                if (!this.periods || !this.computedState.selectedPeriods.length) return;

                const offered = new Set(options.map((option) => option.key));
                const kept = this.computedState.selectedPeriods.filter((key) => offered.has(key));
                if (kept.length !== this.computedState.selectedPeriods.length) this.applySelection(kept);
            },
        },
        nodes: {
            immediate: true,
            handler(nodes: CheckboxTreeNode[]) {
                // Open the newest year so the user is not met with a fully collapsed tree.
                if (nodes.length && !this.expandedYears.length) {
                    this.expandedYears = [nodes[0].key];
                }
            },
        },
    },
    methods: {
        /**
         * Switching granularity clears the selection.
         *
         * Keys do not carry across - "2026-01" is not a quarter - so anything kept would be
         * rejected by the API's selectable-period check. Clearing says so immediately rather than
         * at the end of the wizard.
         */
        applyPeriodType(periodType: FarmToFarmPeriodType) {
            if (periodType === this.computedState.periodType) return;

            Object.assign(this.computedState, {
                periodType,
                selectedPeriods: [],
                periodLabels: {},
            });
            this.expandedYears = [];
        },
        /**
         * Kept chronological, so the period tabs and the workbook come out in reading order.
         *
         * The labels are captured here because the report response carries only keys, and the
         * report step has no period list of its own to look them up in.
         */
        applySelection(keys: string[]) {
            const labels: Record<string, string> = {};
            this.options.forEach((option) => {
                if (keys.includes(option.key)) labels[option.key] = option.label;
            });

            Object.assign(this.computedState, {
                selectedPeriods: [...keys].sort((a, b) => a.localeCompare(b)),
                periodLabels: labels,
            });
        },
    },
});
</script>

<style scoped>
/* Mirrors .animal-group-choice on the farm step, so the two pickers read as one family. */
.period-type-choice button {
    border-width: 1px;
    border-radius: 0.5rem;
    @apply border-opacity-20;
    padding: 0.5rem 1.25rem 0.5rem 1.25rem;
    font-weight: 600;
    white-space: nowrap;
}

.period-type-choice button.--active {
    @apply bg-primary-800;
    @apply border-primary-700;
    color: white;
}
</style>
